import React, { useState } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { useIsMobile, isoToLocalInputValue, localInputValueToIso } from "../shared/utils";
import { generateFlowTestPDF } from "../generateFlowTestPDF";

async function getOrCreateFolder(site, name, parentId) {
  const parentParam = parentId ? `&parent=${parentId}` : "";
  const foldersRes = await axios.get(`${API}/site-folders/?site=${site}${parentParam}`);
  let folder = foldersRes.data.find((f) => f.name === name);
  if (!folder) {
    // Race-safe get-or-create — see the same pattern in InspectionSheet.js
    try {
      const cr = await axios.post(`${API}/site-folders/`, {
        site, name, is_system: true, ...(parentId ? { parent: parentId } : {}),
      });
      folder = cr.data;
    } catch (e) {
      const retryRes = await axios.get(`${API}/site-folders/?site=${site}${parentParam}`);
      folder = retryRes.data.find((f) => f.name === name);
      if (!folder) throw e;
    }
  }
  return folder;
}

async function saveFlowTestBlobToFolder(ft, blob) {
  try {
    const year = ft.inspection_date ? new Date(ft.inspection_date).getFullYear() : new Date().getFullYear();
    // Flow tests only ever come from annual inspections, so the parent
    // folder is always "Annual {year}" — nest the FHR folder inside it
    // rather than leaving it as a root-level sibling.
    const annualFolder = await getOrCreateFolder(ft.site, `Annual ${year}`, null);
    const folder = await getOrCreateFolder(ft.site, `FHR Flow Test ${year}`, annualFolder.id);
    const siteSlug = (ft.site_name || "site").replace(/[^a-z0-9]/gi, "_").slice(0, 60);
    const fileName = `${siteSlug}_FHR_FlowTest_${year}.pdf`;
    const existingRes = await axios.get(`${API}/site-documents/?flow_test=${ft.id}`);
    const existing = (existingRes.data || []).find((d) => d.name === fileName);
    if (existing) {
      if (existing.locked) return true;
      await axios.delete(`${API}/site-documents/${existing.id}/`);
    }
    const pdfFile = new File([blob], fileName, { type: "application/pdf" });
    const fd = new FormData();
    fd.append("file", pdfFile);
    fd.append("folder", folder.id);
    fd.append("flow_test", ft.id);
    await axios.post(`${API}/site-documents/`, fd);
    return true;
  } catch (e) {
    console.warn("Auto-file flow test report failed:", e);
    return false;
  }
}

// Fixed 4-reading, 2-minute test procedure: readings at 0:00, 0:40, 1:20, 2:00
const READING_TIMES = [0, 40, 80, 120];
const timeLabel = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
const readingRowHeaderStyle = {
  display: "grid",
  gridTemplateColumns: "60px 1fr 1fr",
  gap: "8px",
};

function initialReadings(flowTest) {
  const existing = flowTest.readings || [];
  return READING_TIMES.map((seconds_elapsed) => {
    const match = existing.find((r) => r.seconds_elapsed === seconds_elapsed);
    return {
      seconds_elapsed,
      flow_rate_ls: match ? String(match.flow_rate_ls) : "",
      pressure_kpa: match ? String(match.pressure_kpa) : "",
    };
  });
}

export function FlowTestEntryModal({ flowTest, onClose, onSaved }) {
  const isMobile = useIsMobile();
  const [readings, setReadings] = useState(() => initialReadings(flowTest));
  const [notes, setNotes] = useState(flowTest.notes || "");
  const [testedAt, setTestedAt] = useState(
    isoToLocalInputValue(flowTest.tested_at || new Date().toISOString())
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [fileWarning, setFileWarning] = useState("");

  const updateReading = (idx, field, value) => {
    setReadings((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const handleSave = async () => {
    const incomplete = readings.some((r) => r.flow_rate_ls === "" || r.pressure_kpa === "");
    if (incomplete) {
      setError("All 4 readings need both a flow rate and a pressure value.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const res = await axios.patch(`${API}/flow-tests/${flowTest.id}/`, {
        readings: readings.map((r) => ({
          seconds_elapsed: r.seconds_elapsed,
          flow_rate_ls: r.flow_rate_ls,
          pressure_kpa: r.pressure_kpa,
        })),
        notes,
        tested_at: localInputValueToIso(testedAt),
      });
      setResult(res.data);
      onSaved(res.data);
      try {
        const blob = await generateFlowTestPDF(res.data, { noDownload: true });
        const filed = await saveFlowTestBlobToFolder(res.data, blob);
        if (!filed) setFileWarning("The report failed to save to Records. It will still be visible here — ask an admin to check.");
      } catch (pdfErr) {
        console.warn("Flow test PDF generation failed:", pdfErr);
        setFileWarning("The report failed to save to Records. It will still be visible here — ask an admin to check.");
      }
    } catch (e) {
      setError("Something went wrong saving the readings. Please try again.");
    }
    setSaving(false);
  };

  const inputStyle = {
    width: "100%",
    padding: "8px 10px",
    fontSize: "15px",
    borderRadius: "6px",
    border: `1px solid ${colors.border}`,
    boxSizing: "border-box",
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "700",
    color: colors.slate,
    textTransform: "uppercase",
    display: "block",
    marginBottom: "6px",
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 399 }}
      />
      <div
        style={
          isMobile
            ? { position: "fixed", bottom: 0, left: 0, right: 0, backgroundColor: colors.white, borderRadius: "16px 16px 0 0", boxShadow: "0 -4px 20px rgba(0,0,0,0.15)", padding: "20px", zIndex: 400, maxHeight: "90vh", overflowY: "auto" }
            : { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", backgroundColor: colors.white, borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", padding: "28px", zIndex: 400, width: "520px", maxHeight: "88vh", overflowY: "auto" }
        }
      >
        {isMobile && <div style={{ width: "40px", height: "4px", borderRadius: "2px", backgroundColor: colors.border, margin: "0 auto 16px" }} />}

        <div style={{ fontSize: "16px", fontWeight: "700", color: colors.darkGrey, marginBottom: "2px" }}>
          Fire Hose Reel Flow Test
        </div>
        <div style={{ fontSize: "13px", color: colors.slate, marginBottom: "18px" }}>
          {flowTest.site_name} — {flowTest.site_address}
        </div>

        {result ? (
          <div>
            <div
              style={{
                textAlign: "center",
                padding: "20px",
                borderRadius: "10px",
                marginBottom: "16px",
                backgroundColor: result.status === "passed" ? "#f0fdf4" : "#fef2f2",
                border: `1px solid ${result.status === "passed" ? "#bbf7d0" : "#fecaca"}`,
              }}
            >
              <div style={{ fontSize: "18px", fontWeight: "700", color: result.status === "passed" ? colors.green : colors.red }}>
                {result.status === "passed" ? "PASSED" : "FAILED"}
              </div>
              <div style={{ fontSize: "13px", color: colors.slate, marginTop: "6px" }}>
                Worst flow: {result.flow_rate_ls} L/s ({result.flow_passes ? "meets" : "below"} {result.min_flow_ls} L/s minimum)
              </div>
              <div style={{ fontSize: "13px", color: colors.slate }}>
                Worst pressure: {result.pressure_kpa} kPa ({result.pressure_passes ? "within" : "outside"} {result.min_pressure_kpa}–{result.max_pressure_kpa} kPa)
              </div>
              <div style={{ fontSize: "11px", color: "#999", marginTop: "4px" }}>
                Based on the lowest of all 4 readings over the 2-minute test
              </div>
            </div>
            {fileWarning && (
              <div style={{ fontSize: "12px", color: colors.amber, backgroundColor: "#fff7ed", padding: "10px 12px", borderRadius: "8px", marginBottom: "12px" }}>
                {fileWarning}
              </div>
            )}
            <button
              onClick={onClose}
              style={{ width: "100%", padding: "12px", borderRadius: "8px", border: "none", backgroundColor: colors.teal, color: "white", fontSize: "14px", fontWeight: "700", cursor: "pointer" }}
            >
              Done
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
            <div style={{ fontSize: "12px", color: colors.slate }}>
              Take 4 readings over a sustained 2-minute test — AS 1851 requires
              min. 0.33 L/s, AS 2441 requires that flow at 220 kPa ± 10% (198–242 kPa),
              held throughout.
            </div>

            <div style={{ ...readingRowHeaderStyle, borderBottom: `1px solid ${colors.border}`, paddingBottom: "6px" }}>
              <div style={labelStyle}>Time</div>
              <div style={labelStyle}>Flow (L/s)</div>
              <div style={labelStyle}>Pressure (kPa)</div>
            </div>
            {readings.map((r, idx) => (
              <div key={r.seconds_elapsed} style={{ display: "grid", gridTemplateColumns: "60px 1fr 1fr", gap: "8px", alignItems: "center" }}>
                <div style={{ fontSize: "13px", fontWeight: "700", color: colors.darkGrey }}>{timeLabel(r.seconds_elapsed)}</div>
                <input
                  type="number"
                  step="0.01"
                  inputMode="decimal"
                  value={r.flow_rate_ls}
                  onChange={(e) => updateReading(idx, "flow_rate_ls", e.target.value)}
                  style={inputStyle}
                  placeholder="0.35"
                />
                <input
                  type="number"
                  step="0.1"
                  inputMode="decimal"
                  value={r.pressure_kpa}
                  onChange={(e) => updateReading(idx, "pressure_kpa", e.target.value)}
                  style={inputStyle}
                  placeholder="215"
                />
              </div>
            ))}

            <div>
              <label style={labelStyle}>Date &amp; Time Tested</label>
              <input
                type="datetime-local"
                value={testedAt}
                onChange={(e) => setTestedAt(e.target.value)}
                style={inputStyle}
              />
            </div>

            <div>
              <label style={labelStyle}>Notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                style={{ ...inputStyle, height: "60px", resize: "vertical", fontSize: "14px" }}
                placeholder="Optional notes about the test"
              />
            </div>

            {error && (
              <div style={{ fontSize: "13px", color: colors.red, backgroundColor: "#fef2f2", padding: "10px 12px", borderRadius: "8px" }}>
                {error}
              </div>
            )}

            <div style={{ display: "flex", gap: "10px", marginTop: "4px" }}>
              <button
                onClick={onClose}
                style={{ flex: 1, padding: "12px", borderRadius: "8px", border: `1px solid ${colors.border}`, backgroundColor: "white", color: colors.darkGrey, fontSize: "14px", fontWeight: "600", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{ flex: 1, padding: "12px", borderRadius: "8px", border: "none", backgroundColor: colors.teal, color: "white", fontSize: "14px", fontWeight: "700", cursor: saving ? "not-allowed" : "pointer", opacity: saving ? 0.7 : 1 }}
              >
                {saving ? "Saving..." : "Save Readings"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
